import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const safe = (v) => (typeof v === 'string' ? v : '');

/**
 * The signed-in coach's contact details: { full, email, phone }.
 * Priority per field: the team_members row (active team first, then any
 * team) -> user_profiles -> auth metadata; email prefers the auth email.
 * This is who DoorDash orders reach — the account carries no per-order
 * recipient, so the driver's contact is always the person ordering.
 */
export function useSignedInContact() {
  const { user, userProfile, activeTeam } = useAuth();
  const [tmRow, setTmRow] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const uid = user?.id || userProfile?.id; // user_profiles.id == auth.users.id
      if (!uid) { if (alive) setTmRow(null); return; }

      try {
        let q = supabase
          .from('team_members')
          .select('full_name, email, phone_number, team_id, user_id, updated_at, joined_at, created_at')
          .eq('user_id', uid);

        if (activeTeam?.id) q = q.eq('team_id', activeTeam.id);

        let { data, error } = await q.order('updated_at', { ascending: false }).limit(1);
        if (error) console.error('team_members fetch error:', error.message);

        let row = Array.isArray(data) ? data[0] : data;

        // Fallback: any team_members row for user if none for active team
        if (!row && activeTeam?.id) {
          const { data: anyRows, error: anyErr } = await supabase
            .from('team_members')
            .select('full_name, email, phone_number, team_id, user_id, updated_at, joined_at, created_at')
            .eq('user_id', uid)
            .order('updated_at', { ascending: false })
            .limit(1);
          if (anyErr) console.error('team_members fallback error:', anyErr.message);
          row = Array.isArray(anyRows) ? anyRows[0] : anyRows;
        }

        if (alive) setTmRow(row || null);
      } catch (e) {
        console.error('Unexpected team_members fetch error:', e);
        if (alive) setTmRow(null);
      }
    })();
    return () => { alive = false; };
  }, [user?.id, userProfile?.id, activeTeam?.id]);

  return useMemo(() => {
    const tmFull  = safe(tmRow?.full_name);
    const tmEmail = safe(tmRow?.email);
    const tmPhone = safe(tmRow?.phone_number);

    const profFull  = [safe(userProfile?.first_name), safe(userProfile?.last_name)].filter(Boolean).join(' ').trim();
    const profEmail = safe(userProfile?.email);
    const profPhone = safe(userProfile?.phone);

    const meta = user?.user_metadata || {};
    const metaFull  = safe(meta.full_name) || safe(meta.name) ||
                      [safe(meta.given_name), safe(meta.family_name)].filter(Boolean).join(' ').trim();
    const metaPhone = safe(meta.phone);

    return {
      full:  tmFull  || profFull  || metaFull  || '',
      phone: tmPhone || profPhone || metaPhone || '',
      email: safe(user?.email) || tmEmail || profEmail || '',
    };
  }, [tmRow, userProfile, user]);
}

export default useSignedInContact;
